import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  Supplier, 
  SupplierListResponse, 
  CreateSupplierRequest, 
  UpdateSupplierRequest,
  SupplierSearchParams,
  SupplierStatistics
} from '../models/SupplierModel';
import * as SupplierCRUD from './SupplierCRUD';

interface SupplierState {
  suppliers: Supplier[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  statistics: SupplierStatistics | null;
  selectedSuppliers: string[];
}

const initialState: SupplierState = {
  suppliers: [],
  loading: false,
  error: null,
  totalCount: 0,
  statistics: null,
  selectedSuppliers: [],
};

// Async thunks
export const fetchSuppliers = createAsyncThunk(
  'suppliers/fetchSuppliers',
  async (params?: SupplierSearchParams) => {
    const response = await SupplierCRUD.getSuppliers(params);
    return response;
  },
  {
    // In React StrictMode (dev), mount effects may run twice.
    // Skip duplicate fetch when the default list request is already loading.
    condition: (params, { getState }) => {
      const state = getState() as { supplier?: SupplierState };
      if (!params && state.supplier?.loading) {
        return false;
      }
      return true;
    }
  }
);

export const fetchSupplierById = createAsyncThunk(
  'suppliers/fetchSupplierById',
  async (id: string) => {
    const response = await SupplierCRUD.getSupplierById(id);
    return response;
  }
);

export const createSupplier = createAsyncThunk(
  'suppliers/createSupplier',
  async (supplierData: CreateSupplierRequest) => {
    const response = await SupplierCRUD.createSupplier(supplierData);
    return response;
  }
);

export const updateSupplier = createAsyncThunk(
  'suppliers/updateSupplier',
  async ({ id, supplierData }: { id: string; supplierData: UpdateSupplierRequest }) => {
    const response = await SupplierCRUD.updateSupplier(id, supplierData);
    return response;
  }
);

export const deleteSupplier = createAsyncThunk(
  'suppliers/deleteSupplier',
  async (id: string) => {
    await SupplierCRUD.deleteSupplier(id);
    return id;
  }
);

export const searchSuppliers = createAsyncThunk(
  'suppliers/searchSuppliers',
  async (params: SupplierSearchParams) => {
    const response = await SupplierCRUD.searchSuppliers(params);
    return response;
  }
);

export const fetchSupplierStatistics = createAsyncThunk(
  'suppliers/fetchStatistics',
  async () => {
    const response = await SupplierCRUD.getSupplierStatistics();
    return response;
  }
);

export const bulkUpdateSuppliers = createAsyncThunk(
  'suppliers/bulkUpdate',
  async (data: { ids: string[]; updates: Partial<UpdateSupplierRequest> }) => {
    const response = await SupplierCRUD.bulkUpdateSuppliers(data);
    return response;
  }
);

export const addSupplierToCompany = createAsyncThunk(
  'suppliers/addToCompany',
  async ({ supplierId, companyId }: { supplierId: string; companyId: string }) => {
    const response = await SupplierCRUD.addSupplierToCompany(supplierId, companyId);
    return response;
  }
);

export const removeSupplierFromCompany = createAsyncThunk(
  'suppliers/removeFromCompany',
  async ({ supplierId, companyId }: { supplierId: string; companyId: string }) => {
    const response = await SupplierCRUD.removeSupplierFromCompany(supplierId, companyId);
    return response;
  }
);

const supplierSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedSuppliers: (state, action: PayloadAction<string[]>) => {
      state.selectedSuppliers = action.payload;
    },
    toggleSupplierSelection: (state, action: PayloadAction<string>) => {
      const supplierId = action.payload;
      const index = state.selectedSuppliers.indexOf(supplierId);
      if (index > -1) {
        state.selectedSuppliers.splice(index, 1);
      } else {
        state.selectedSuppliers.push(supplierId);
      }
    },
    clearSelectedSuppliers: (state) => {
      state.selectedSuppliers = [];
    },
    addSupplier: (state, action: PayloadAction<Supplier>) => {
      state.suppliers.push(action.payload);
      state.totalCount += 1;
    },
    updateSupplierInList: (state, action: PayloadAction<Supplier>) => {
      const index = state.suppliers.findIndex(supplier => supplier.id === action.payload.id);
      if (index !== -1) {
        state.suppliers[index] = action.payload;
      }
    },
    removeSupplierFromList: (state, action: PayloadAction<string>) => {
      const supplierId = action.payload;
      state.suppliers = state.suppliers.filter(supplier => supplier.id !== supplierId);
      state.totalCount = Math.max(0, state.totalCount - 1);
      state.selectedSuppliers = state.selectedSuppliers.filter(id => id !== supplierId);
    },
    setSuppliers: (state, action: PayloadAction<Supplier[]>) => {
      state.suppliers = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch suppliers
    builder
      .addCase(fetchSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload)) {
          state.suppliers = action.payload;
          state.totalCount = action.payload.length;
        } else {
          state.suppliers = action.payload.results || [];
          state.totalCount = action.payload.count || 0;
        }
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch suppliers';
      });

    // Fetch supplier by ID
    builder
      .addCase(fetchSupplierById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.suppliers.findIndex(supplier => supplier.id === action.payload.id);
        if (index !== -1) {
          state.suppliers[index] = action.payload;
        } else {
          state.suppliers.push(action.payload);
          state.totalCount += 1;
        }
      })
      .addCase(fetchSupplierById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch supplier';
      });

    // Create supplier
    builder
      .addCase(createSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSupplier.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers.push(action.payload);
        state.totalCount += 1;
      })
      .addCase(createSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create supplier';
      });

    // Update supplier
    builder
      .addCase(updateSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.suppliers.findIndex(supplier => supplier.id === action.payload.id);
        if (index !== -1) {
          state.suppliers[index] = action.payload;
        }
      })
      .addCase(updateSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update supplier';
      });

    // Delete supplier
    builder
      .addCase(deleteSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSupplier.fulfilled, (state, action) => {
        state.loading = false;
        const supplierId = action.payload;
        state.suppliers = state.suppliers.filter(supplier => supplier.id !== supplierId);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.selectedSuppliers = state.selectedSuppliers.filter(id => id !== supplierId);
      })
      .addCase(deleteSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete supplier';
      });

    // Search suppliers
    builder
      .addCase(searchSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers = action.payload.results || [];
        state.totalCount = action.payload.count || 0;
      })
      .addCase(searchSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search suppliers';
      });

    // Fetch statistics
    builder
      .addCase(fetchSupplierStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchSupplierStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch statistics';
      });

    // Bulk update
    builder
      .addCase(bulkUpdateSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateSuppliers.fulfilled, (state) => {
        state.loading = false;
        state.selectedSuppliers = [];
      })
      .addCase(bulkUpdateSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to bulk update suppliers';
      });

    // Add to company
    builder
      .addCase(addSupplierToCompany.fulfilled, (state, action) => {
        // Handle success if needed
      })
      .addCase(addSupplierToCompany.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add supplier to company';
      });

    // Remove from company
    builder
      .addCase(removeSupplierFromCompany.fulfilled, (state, action) => {
        // Handle success if needed
      })
      .addCase(removeSupplierFromCompany.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to remove supplier from company';
      });
  },
});

export const {
  clearError,
  setSelectedSuppliers,
  toggleSupplierSelection,
  clearSelectedSuppliers,
  addSupplier,
  updateSupplierInList,
  removeSupplierFromList,
  setSuppliers,
  setLoading,
  setError,
} = supplierSlice.actions;

export const { reducer } = supplierSlice;
export default supplierSlice.reducer;
